import React from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import InventoryIcon from '@mui/icons-material/Inventory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ToolbarPopover from 'components/layout/ToolbarPopover';
import { ScreenKey } from 'constant/ScreenPermission';

type MenuItem = {
  path: string;
  icon: React.ReactNode;
  title: string;
  color: string;
  /** この項目に必要な権限 (undefinedなら常に表示) */
  screenKey?: ScreenKey;
};

const menuItems: MenuItem[] = [
  {
    path: '/',
    icon: <HomeIcon />,
    title: 'Home',
    color: '#9E9E9E',
  },
  {
    path: '/security',
    icon: <SecurityIcon />,
    title: 'セキュリティ',
    color: '#2e7d32',
    screenKey: ScreenKey.SECURITY_TOP,
  },
  {
    path: '/member',
    icon: <PeopleIcon />,
    title: '要員管理',
    color: '#1565c0',
    screenKey: ScreenKey.MEMBER_TOP,
  },
  {
    path: '/contract',
    icon: <DescriptionIcon />,
    title: '契約管理',
    color: '#6a1b9a',
    screenKey: ScreenKey.CONTRACT_TOP,
  },
  {
    path: '/ticket',
    icon: <ConfirmationNumberIcon />,
    title: 'チケット管理',
    color: '#c2185b',
    screenKey: ScreenKey.TICKET_TOP,
  },
  {
    path: '/assessment',
    icon: <InventoryIcon />,
    title: '資産管理',
    color: '#283593',
    screenKey: ScreenKey.ASSESSMENT_TOP,
  },
  {
    path: '/workspace',
    icon: <SchoolIcon />,
    title: 'ワークスペース',
    color: '#00838f',
    screenKey: ScreenKey.TRAINING_TOP,
  },
  {
    path: '/training-learn',
    icon: <ModelTrainingIcon />,
    title: '研修',
    color: '#0097a7',
    screenKey: ScreenKey.TRAINING_LEARN_TOP,
  },
  {
    path: '/training-admin',
    icon: <TuneIcon />,
    title: '研修管理',
    color: '#00695c',
    screenKey: ScreenKey.TRAINING_ADMIN_TOP,
  },
  {
    path: '/devdocs',
    icon: <ArticleIcon />,
    title: 'Dev Insights',
    color: '#1e3a8a',
  },
  {
    path: '/management',
    icon: <AdminPanelSettingsIcon />,
    title: '管理者',
    color: '#00695c',
    screenKey: ScreenKey.MGMT_TOP,
  },
  {
    path: '/guide',
    icon: <MenuBookIcon />,
    title: '利用ガイド',
    color: '#e65100',
  },
];

type DashboardPopupProps = {
  allowedScreens: ScreenKey[];
};

/**
 * ヘッダー内のダッシュボードアイコンから各ドメインTopに遷移するポップアップ。
 * allowedScreens に基づいて表示項目をフィルタする。
 */
const DashboardPopup: React.FC<DashboardPopupProps> = ({ allowedScreens }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const visibleItems = menuItems.filter(
    (item) => !item.screenKey || allowedScreens.includes(item.screenKey),
  );

  return (
    <>
      <Tooltip title="ダッシュボード">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <DashboardIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>
      <ToolbarPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        width={220}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Stack spacing={0.75} sx={{ p: 1.25 }}>
          {visibleItems.map((item) => (
            <Card
              key={item.path}
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.15s',
                '&:hover': { borderColor: item.color, bgcolor: 'action.hover' },
              }}
            >
              <CardActionArea
                onClick={() => {
                  navigate(item.path);
                  setAnchorEl(null);
                }}
                sx={{ px: 1.25, py: 1 }}
              >
                {/* Home メニューと同じ横並び・アイコンチップ */}
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${item.color}14`,
                      color: item.color,
                    }}
                  >
                    {React.cloneElement(item.icon as React.ReactElement<{ sx?: object }>, {
                      sx: { fontSize: 18 },
                    })}
                  </Box>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                    {item.title}
                  </Typography>
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      </ToolbarPopover>
    </>
  );
};

export default DashboardPopup;
